import type { GameModeId, MatchSummary } from '@sol-pool/engine-rules'
import { formatAmount } from '@sol-pool/protocol'
import { localeDeFormatacao, t, traduzirFim, traduzirMotivoDoFim } from '@/i18n'
import { enderecoCurto } from '@/game/objects/placar'
import { esc } from '@/ui/esc'

/**
 * O cartão do fim da partida: o que vai escrito nele.
 *
 * "Você venceu / por regras" era tudo o que a tela dizia, e os jogadores
 * pediram um resultado que dê para fotografar e postar: quem jogou, quanto
 * fez cada um, quanto durou, quanto valia. Este módulo decide TODO o texto do
 * cartão — a tela HTML e a imagem 1080×1080 desenham os mesmos dados, e por
 * isso nunca discordam entre si.
 *
 * Puro de propósito: sem DOM, sem canvas. É o que permite testar que o
 * vencedor vem do SERVIDOR na mesa apostada (uma desistência termina a partida
 * sem as regras terem terminado), que a duração some quando não se sabe (em
 * vez de mostrar 0 s), e que a aposta só aparece quando houve aposta.
 */

export type EntradaDoCartao = {
  resumo: MatchSummary
  modo: GameModeId
  /**
   * A partida em rede: quem sou, a carteira do adversário e o desfecho
   * segundo o servidor. Nulo no treino.
   *
   * `winner` e `reason` são os do servidor, não os das regras: por
   * desistência ou tempo o servidor encerra sem a mesa ter chegado ao fim.
   */
  rede: {
    you: 0 | 1 | null
    opponent: string
    winner: 0 | 1 | null
    reason: string
    /** Apelidos e carteiras dos dois, na ordem dos índices, quando se sabe. */
    names?: [string | null, string | null] | undefined
    players?: [string, string] | undefined
  } | null
  /** Quando a partida começou, em ms — ou nulo se não se sabe (página recarregada no meio). */
  inicio: number | null
  /** Quando terminou, em ms. Também é a data que vai no cartão. */
  fim: number
  tacadas: number
  /** Entrada por jogador, em lamports. Nulo fora da mesa apostada (e no demo). */
  stakeLamports: string | null
  simbolo: string
}

export type JogadorDoCartao = {
  nome: string
  /** Carteira abreviada, só na rede e só do adversário. */
  sub: string | null
  /** O número grande: bolas encaçapadas no 8-Ball, pontos na sinuca. "—" quando não há como contar. */
  numero: string
  legenda: string
  vencedor: boolean
}

export type DadosDoCartao = {
  titulo: string
  /** Cor do cartão: verde, vermelho, neutro (anulada) ou dourado (treino, sem "você"). */
  tom: 'venceu' | 'perdeu' | 'anulada' | 'neutro'
  modalidade: string
  /** "Mesa apostada" ou "Mesa de treino". */
  tipoDeMesa: string
  jogadores: [JogadorDoCartao, JogadorDoCartao]
  /** A regra que encerrou ("A bola 8 foi encaçapada corretamente"), ou nulo. */
  motivo: string | null
  /** Código da regra, para o link da página de regras. */
  motivoCodigo: string | null
  /** Como o servidor encerrou ("por desistência"). Só na rede. */
  como: string | null
  /** "12 min 30 s", ou nulo quando o início não é conhecido. */
  duracao: string | null
  tacadas: number
  /** "0.05 SOL cada · pote 0.1 SOL", ou nulo. */
  aposta: string | null
  data: string
}

/** Bolas de cada grupo do 8-Ball. É daqui que sai "encaçapou 5". */
const BOLAS_POR_GRUPO = 7

export function dadosDoCartao(e: EntradaDoCartao): DadosDoCartao {
  const { resumo, rede } = e
  const vencedor = rede ? rede.winner : resumo.winner

  // Título e tom. Na rede há um "você"; no treino — e para quem só ASSISTE a
  // rede (`you` nulo) — só o número do jogador, em tom neutro.
  const jogo = rede !== null && rede.you !== null
  let titulo: string
  let tom: DadosDoCartao['tom']
  if (vencedor === null) {
    titulo = t('net.partidaAnulada')
    tom = 'anulada'
  } else if (jogo) {
    const venci = vencedor === rede.you
    titulo = venci ? t('net.voceVenceu') : t('net.vocePerdeu')
    tom = venci ? 'venceu' : 'perdeu'
  } else {
    titulo = t('cartao.venceuJogador', { n: vencedor + 1 })
    tom = 'neutro'
  }

  const jogador = (i: 0 | 1): JogadorDoCartao => {
    const eu = jogo && rede.you === i
    // Como no placar: apelido quando há, com a carteira embaixo. Ver `placar.ts`.
    const apelido = rede?.names?.[i] ?? null
    const nome = jogo
      ? eu
        ? t('placar.voce')
        : (apelido ?? t('placar.adversario'))
      : (apelido ?? (i === 0 ? t('placar.jogador1') : t('placar.jogador2')))
    const carteira = jogo && !eu ? rede.opponent : (rede?.players?.[i] ?? null)

    let numero = '—'
    let legenda = ''
    if (resumo.score) {
      numero = String(resumo.score[i])
      legenda = t('placar.pontos', { count: resumo.score[i] })
    } else if (resumo.suas) {
      // O que cada um encaçapou é o grupo inteiro menos o que ainda falta.
      const feitas = BOLAS_POR_GRUPO - resumo.suas[i].length
      numero = String(feitas)
      legenda = t('cartao.encacapadas')
    } else {
      // Terminou com a mesa aberta (desistência cedo, tempo): não há o que contar.
      legenda = t('placar.mesaAberta')
    }

    return {
      nome,
      sub: !eu && carteira ? enderecoCurto(carteira) : null,
      numero,
      legenda,
      vencedor: vencedor === i,
    }
  }

  // "Você" à esquerda, como no placar durante a partida.
  const esquerda: 0 | 1 = rede?.you ?? 0
  const direita: 0 | 1 = esquerda === 0 ? 1 : 0

  const motivoCodigo = resumo.ending ?? null

  return {
    titulo,
    tom,
    modalidade: t(`modo.${e.modo}.nome` as `modo.${GameModeId}.nome`),
    tipoDeMesa: e.stakeLamports !== null ? t('cartao.mesaApostada') : t('cartao.treino'),
    jogadores: [jogador(esquerda), jogador(direita)],
    motivo: traduzirMotivoDoFim(motivoCodigo),
    motivoCodigo,
    como: rede ? t('net.por', { motivo: traduzirFim(rede.reason) }) : null,
    duracao: e.inicio !== null && e.fim >= e.inicio ? formatarDuracao(e.fim - e.inicio) : null,
    tacadas: e.tacadas,
    aposta: e.stakeLamports !== null ? textoDaAposta(e.stakeLamports, e.simbolo) : null,
    data: formatarData(e.fim),
  }
}

/**
 * Duração em texto curto: "45 s", "12 min 30 s", "1 h 05 min".
 *
 * Arredonda para baixo — uma partida de 59,9 s durou 59 s, não 60. E nunca
 * devolve texto para um valor inválido: quem chama já filtrou o "não sei".
 */
export function formatarDuracao(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000))
  const h = Math.floor(total / 3600)
  const min = Math.floor((total % 3600) / 60)
  const s = total % 60
  if (h > 0) return t('duracao.horas', { h, min: String(min).padStart(2, '0') })
  if (min > 0) return t('duracao.minutos', { min, s })
  return t('duracao.segundos', { s })
}

/** "0.05 SOL cada · pote 0.1 SOL". O pote é o dobro da entrada; a taxa não entra aqui. */
export function textoDaAposta(stakeLamports: string, simbolo: string): string {
  const entrada = BigInt(stakeLamports)
  return t('cartao.apostaValor', {
    valor: formatAmount(entrada),
    pote: formatAmount(entrada * 2n),
    simbolo,
  })
}

function formatarData(ms: number): string {
  try {
    return new Intl.DateTimeFormat(localeDeFormatacao(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(ms))
  } catch {
    return new Date(ms).toISOString().slice(0, 16).replace('T', ' ')
  }
}

// ------------------------------------------------------------------- HTML

/**
 * O que cada tela põe no cartão além do resultado.
 *
 * `status` é o que aconteceu com o dinheiro (a liquidação, na mesa apostada),
 * numa caixa discreta logo depois das estatísticas. `principal` é o próximo
 * passo — "Voltar ao lobby", "Nova partida" — e ganha o botão grande: antes
 * era um link cinza no meio do cartão, abaixo da liquidação e acima dos
 * botões de imagem, e passava despercebido justamente no que mais se clica.
 */
export type ExtrasDoCartao = {
  status?: string
  principal?: string
}

/**
 * O cartão em HTML, para a sobreposição da mesa.
 *
 * A ordem é a da leitura: o resultado, o placar, os números, o que houve com
 * o dinheiro, o que fazer agora e, por último, compartilhar. Os extras vêm
 * como HTML já pronto porque cada tela tem o seu.
 */
export function htmlDoCartao(d: DadosDoCartao, extras: ExtrasDoCartao = {}): string {
  const jogador = (j: JogadorDoCartao) => `
    <div class="cartao-jogador" data-vencedor="${j.vencedor}">
      ${j.vencedor ? `<div class="cartao-coroa">${esc(t('cartao.vencedor'))}</div>` : ''}
      <div class="cartao-nome">${esc(j.nome)}${j.sub ? `<span class="cartao-sub">${esc(j.sub)}</span>` : ''}</div>
      <div class="cartao-numero"${j.numero === '—' ? ' data-vazio' : ''}>${esc(j.numero)}</div>
      <div class="cartao-legenda">${esc(j.legenda)}</div>
    </div>`

  const estatistica = (rotulo: string, valor: string | null) =>
    valor === null
      ? ''
      : `<div class="cartao-stat"><span>${esc(rotulo)}</span><strong>${esc(valor)}</strong></div>`

  // "por regras" e a regra que encerrou dizem a mesma coisa em dois níveis:
  // vão numa linha só, com o "como" em destaque e a regra embaixo.
  const regra = d.motivo
    ? `${esc(d.motivo)}${
        d.motivoCodigo
          ? ` <a class="cartao-regras" href="/regras.html#fim-${esc(d.motivoCodigo)}">(${esc(t('fim.regras'))})</a>`
          : ''
      }`
    : ''

  return `<div class="fim cartao" data-tom="${d.tom}">
    <div class="cartao-marca">SOL Pool <span>· ${esc(d.modalidade)} · ${esc(d.tipoDeMesa)}</span></div>
    <div class="cartao-cabeca">
      <p class="fim-titulo">${esc(d.titulo)}</p>
      ${d.como ? `<p class="cartao-como">${esc(d.como)}</p>` : ''}
      ${regra ? `<p class="fim-motivo">${regra}</p>` : ''}
    </div>
    <div class="cartao-placar">
      ${jogador(d.jogadores[0])}
      <div class="cartao-x">×</div>
      ${jogador(d.jogadores[1])}
    </div>
    <div class="cartao-stats">
      ${estatistica(t('cartao.duracao'), d.duracao)}
      ${estatistica(t('cartao.tacadas'), String(d.tacadas))}
      ${estatistica(t('cartao.aposta'), d.aposta)}
    </div>
    <div class="cartao-data">${esc(d.data)}</div>
    ${extras.status ? `<div class="cartao-status">${extras.status}</div>` : ''}
    ${extras.principal ? `<div class="cartao-principal">${extras.principal}</div>` : ''}
    <div class="cartao-compartilhar">
      <span class="cartao-compartilhar-rotulo">${esc(t('cartao.compartilhar'))}</span>
      <div class="cartao-acoes">
        <button type="button" class="ghost" data-acao="copiar">${esc(t('cartao.copiar'))}</button>
        <button type="button" class="ghost" data-acao="baixar">${esc(t('cartao.baixar'))}</button>
      </div>
    </div>
    <p class="cartao-aviso" data-aviso hidden></p>
  </div>`
}
